/**
 * MarketTicker - slim, light live-market strip ("Data Product" v3).
 * A pinned LIVE badge (pulsing green dot) followed by a scrolling row of
 * commodity/market moves with delta-coloured figures on a soft grey surface.
 * Pauses on hover. Respects prefers-reduced-motion (static scrollable row).
 * The `variant` prop is kept for API compatibility; both render light.
 */

import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useReducedMotion } from 'framer-motion';
import { useThemeMode, numericSx } from '../../context/ThemeContext';

// Map usePriceMovements() rows → ticker items. Sorted by move size, zero-change
// rows dropped (a wall of "▲ 0.0%" reads as noise, not signal).
export function movementsToTicker(pm = []) {
  return (pm || [])
    .filter((m) => m && (m.topCommodity || m.commodity || m.marketName))
    .map((m) => ({
      label: m.topCommodity || m.commodity || m.marketName,
      sub: m.topCommodity ? m.marketName : m.state,
      change: Number(m.priceChange ?? m.change ?? 0),
    }))
    .filter((it) => Math.abs(it.change) >= 0.05)
    .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
    .slice(0, 14);
}

function MarketTicker({ items = [], live = true, variant = 'bar', speed = 64 }) {
  const { palette } = useThemeMode();
  const reduce = useReducedMotion();

  // rAF-driven marquee instead of a CSS animation: a CSS animation is
  // time-anchored, so when Chrome freezes rendering for an occluded tab and
  // later restores it, the strip snaps to where the clock says it should be
  // (which reads as a "reset" right as the user mouses in). rAF stops while
  // hidden and the capped time-step resumes from the same pixel.
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const pausedRef = useRef(false);
  useEffect(() => {
    const el = trackRef.current;
    if (!el || reduce) return undefined;
    let half = el.scrollWidth / 2;
    const ro = new ResizeObserver(() => {
      half = el.scrollWidth / 2;
    });
    ro.observe(el);
    let raf;
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(now - last, 100); // cap: hidden-tab gaps never fast-forward
      last = now;
      if (!pausedRef.current && half > 0) {
        posRef.current = (posRef.current + (half / (speed * 1000)) * dt) % half;
        el.style.transform = `translateX(${-posRef.current}px)`;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [speed, reduce]);

  if (!items || items.length === 0) return null;

  const renderRow = (prefix, ariaHidden = false) =>
    items.map((it, i) => {
      // Same two traps as DeltaChip. Flat counted as up, so an unchanged
      // price got a green up arrow and read as a gain; and the direction came
      // from the raw value while the figure was rounded separately, so a real
      // move too small to show at one decimal read "▲ 0.0%", an arrow beside
      // a figure denying it.
      const v = Number(it.change) || 0;
      const up = v > 0;
      const flat = v === 0;
      const roundsToZero = !flat && Number(Math.abs(v).toFixed(1)) === 0;
      return (
        <Box
          key={`${prefix}-${i}`}
          component="span"
          aria-hidden={ariaHidden || undefined}
          sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, px: 2.25, borderRight: `1px solid ${palette.divider}` }}
        >
          <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, color: palette.text }}>
            {it.label}
          </Typography>
          {it.sub && (
            <Typography component="span" sx={{ fontSize: '0.7rem', color: palette.textMuted }}>
              {it.sub}
            </Typography>
          )}
          {it.value && (
            <Typography component="span" sx={{ ...numericSx, fontSize: '0.76rem', fontWeight: 600, color: palette.text }}>
              {it.value}
            </Typography>
          )}
          <Typography component="span" sx={{ ...numericSx, fontSize: '0.74rem', fontWeight: 500, color: flat ? palette.textMuted : (up ? palette.accent : palette.danger) }}>
            {flat ? '' : `${up ? '▲' : '▼'} `}{roundsToZero ? '<0.1' : Math.abs(v).toFixed(1)}%
          </Typography>
        </Box>
      );
    });

  return (
    <Box
      role="marquee"
      aria-label="Live market movements"
      // Pause from anywhere on the strip (badge and fade zones included).
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
        bgcolor: palette.surface,
        borderBottom: `1px solid ${palette.divider}`,
        height: 38,
      }}
    >
      {live && (
        <Box
          sx={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.85,
            px: { xs: 1.75, md: 2.5 },
            zIndex: 2,
            bgcolor: palette.surface,
            boxShadow: `14px 0 14px -10px ${palette.surface}`,
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: palette.accentStrong,
              animation: reduce ? 'none' : 'tickerPulse 2s infinite',
              '@keyframes tickerPulse': {
                '0%': { boxShadow: `0 0 0 0 ${palette.accentStrong}59` },
                '70%': { boxShadow: '0 0 0 6px transparent' },
                '100%': { boxShadow: '0 0 0 0 transparent' },
              },
            }}
          />
          <Typography component="span" sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: palette.text }}>
            Live
          </Typography>
        </Box>
      )}

      {/* Clip window */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        {reduce ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              width: '100%',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {renderRow('static')}
          </Box>
        ) : (
          <Box
            className="ticker-track"
            ref={trackRef}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              willChange: 'transform',
            }}
          >
            {renderRow('a')}
            {renderRow('b', true)}
          </Box>
        )}
      </Box>

      {/* Right fade */}
      <Box aria-hidden sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 56, zIndex: 2, pointerEvents: 'none', background: `linear-gradient(270deg, ${palette.surface}, transparent)` }} />
    </Box>
  );
}

export default MarketTicker;
